import { isThemePreview } from '../../api/previewVisit';
import { useCommerce, type Commerce } from '../../commerce/ShopCommerce';
import { CartLineList } from '../../commerce/CartLines';
import { CartStatus, EmptyCart } from '../../commerce/CartStates';
import { FreeShippingBar } from '../../commerce/FreeShippingBar';
import { OrderNote } from '../../commerce/OrderNote';
import { sampleCommerce } from '../../commerce/sampleCart';
import { useGoToCheckout } from '../../commerce/useGoToCheckout';
import { useElementBox, useElementRender, type ElementComponent } from './elementRenderContext';
import { placeholderProducts } from './placeholderCatalog';
import { sampleProducts } from './sampleCatalog';
import type { ElementProps } from './elementProps';
import '../../commerce/cartDrawer.css';
import './elementCartPage.css';
import { shopWords } from '../../commerce/shopWords';
import { CartEstimate } from '../../commerce/CartEstimate';

// The cart page, as Sleek's /cart: the free-delivery goal under the heading, the lines with their steppers
// on the start side, and on the end side the order note, the estimated total and Check out. It draws the
// same cart as the drawer, so a change in one is in the other. The builder and a preview show two samples.

// Gives the cart to draw: the shopper's own, or in the builder and a preview while it is empty, two of the theme's samples or stand-ins.
function useShownCart(props: ElementProps): Commerce | null {
  const commerce = useCommerce();
  const { editing } = useElementRender();
  if (!commerce) return null;
  return (editing || isThemePreview()) && commerce.lines.length === 0 ? sampleCommerce(commerce, sampleProducts(props, 2).length > 0 ? sampleProducts(props, 2) : placeholderProducts(2)) : commerce;
}

// Draws the summary beside the lines: the note, the estimated total and the way on to checkout.
const CartSummary: React.FC<{ commerce: Commerce }> = ({ commerce }) => {
  const goToCheckout = useGoToCheckout(commerce);
  return (
    <aside className="xe-cart-page-summary" aria-label={shopWords(commerce.language).orderSummary}>
      <OrderNote commerce={commerce} />
      <CartStatus commerce={commerce} />
      <CartEstimate commerce={commerce} />
      <p className="xe-cart-note">{shopWords(commerce.language).deliveryAtCheckout}</p>
      <button type="button" className="xe-cart-primary" disabled={!commerce.quote || commerce.loading || commerce.error !== ''} onClick={goToCheckout}>{shopWords(commerce.language).checkOut}</button>
    </aside>
  );
};

// Draws the cart page: its lines and summary, or the empty cart with collections to start from.
export const CartPageElement: ElementComponent = ({ node }) => {
  const box = useElementBox(node);
  const commerce = useShownCart(node.props);
  const className = [box.className, 'xe-cart-page'].filter(Boolean).join(' ');
  if (!commerce) return <div {...box} className={className} />;
  if (commerce.lines.length === 0) return <div {...box} className={className}><EmptyCart /></div>;
  return (
    <div {...box} className={className}>
      <FreeShippingBar commerce={commerce} />
      <div className="xe-cart-page-grid">
        <CartLineList commerce={commerce} editable />
        <CartSummary commerce={commerce} />
      </div>
    </div>
  );
};
